import { PluginAuthorization } from "@dashboard/extensions/views/EditPluginExtension/components/PluginAuthorization";
import { PluginInfo } from "@dashboard/extensions/views/EditPluginExtension/components/PluginInfo";
import { PluginDetailsPageFormData } from "@dashboard/extensions/views/EditPluginExtension/components/PluginsDetailsPage";
import { PluginSettings } from "@dashboard/extensions/views/EditPluginExtension/components/PluginSettings/PluginSettings";
import { getConfigurationInput } from "@dashboard/extensions/views/EditPluginExtension/EditPluginExtension";
import {
  isSecretField,
} from "@dashboard/extensions/views/EditPluginExtension/utils";
import { usePluginQuery, usePluginUpdateMutation } from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { Box, Button } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { ExtensionLoadingState } from "./ExtensionLoadingState";
import { extensionConfigMessages } from "./messages";

interface PluginConfigViewProps {
  pluginId: string;
  onBack: () => void;
}

export const PluginConfigView: React.FC<PluginConfigViewProps> = ({
  pluginId,
  onBack,
}) => {
  const intl = useIntl();
  const notify = useNotifier();
  const [editingField, setEditingField] = useState<string | null>(null);

  const { data: pluginData, loading } = usePluginQuery({
    variables: { id: pluginId },
  });

  const [pluginUpdate, pluginUpdateOpts] = usePluginUpdateMutation({
    onCompleted: data => {
      if (data?.pluginUpdate?.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges),
        });
      }
    },
  });

  const plugin = pluginData?.plugin;
  const globalConfig = plugin?.globalConfiguration;
  const selectedConfig = globalConfig
    ? globalConfig
    : plugin?.channelConfigurations?.[0];

  const [formData, setFormData] =
    useState<PluginDetailsPageFormData | null>(null);

  const currentData: PluginDetailsPageFormData = formData ?? {
    active: selectedConfig?.active ?? false,
    configuration:
      selectedConfig?.configuration
        ?.filter(
          field =>
            !isSecretField(
              selectedConfig?.configuration || [],
              field.name,
            ),
        )
        .map(field => ({
          ...field,
          value: field.value || "",
        })) ?? [],
  };

  if (loading || !plugin) {
    return (
      <Box display="flex" flexDirection="column" gap={3}>
        <Box>
          <Button variant="tertiary" type="button" onClick={onBack}>
            {intl.formatMessage(extensionConfigMessages.backToList)}
          </Button>
        </Box>
        <ExtensionLoadingState
          message={intl.formatMessage(extensionConfigMessages.loadingPluginConfiguration)}
        />
      </Box>
    );
  }

  const handleChange = (event: { target: { name: string; value: any } }) => {
    const { name, value } = event.target;
    const newData: PluginDetailsPageFormData = {
      active: name === "active" ? value : currentData.active,
      configuration: currentData.configuration.map(configItem =>
        configItem.name === name ? { ...configItem, value } : configItem,
      ),
    };

    setFormData(newData);
  };

  const handleSave = async () => {
    const channelId = selectedConfig?.channel?.id ?? null;

    await pluginUpdate({
      variables: {
        channelId,
        id: pluginId,
        input: {
          active: currentData.active,
          configuration: getConfigurationInput(
            selectedConfig?.configuration ?? null,
            currentData.configuration,
          ),
        },
      },
    });
  };

  const handleFieldUpdate = (value: string | null) => {
    if (!editingField) return;

    const channelId = selectedConfig?.channel?.id ?? null;

    pluginUpdate({
      variables: {
        channelId,
        id: pluginId,
        input: {
          configuration: [{ name: editingField, value }],
        },
      },
    });
    setEditingField(null);
  };

  const isSubmitting = pluginUpdateOpts.loading;
  const formErrors = pluginUpdateOpts.data?.pluginUpdate?.errors || [];

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box>
        <Button variant="tertiary" type="button" onClick={onBack}>
          {intl.formatMessage(extensionConfigMessages.backToList)}
        </Button>
      </Box>
      <PluginInfo
        data={currentData}
        description={plugin.description}
        errors={formErrors}
        name={plugin.name}
        onChange={handleChange}
        disabled={isSubmitting}
      />
      {currentData.configuration && currentData.configuration.length > 0 && (
        <PluginSettings
          data={currentData}
          errors={formErrors}
          disabled={isSubmitting}
          onChange={handleChange}
        />
      )}
      {selectedConfig?.configuration?.some(field =>
        isSecretField(selectedConfig.configuration ?? [], field.name),
      ) && (
        <PluginAuthorization
          fields={selectedConfig.configuration ?? []}
          onClear={() => handleFieldUpdate(null)}
          onEdit={f => setEditingField(f)}
        />
      )}
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="primary"
          type="button"
          onClick={handleSave}
          disabled={isSubmitting}
        >
          {intl.formatMessage(extensionConfigMessages.saveConfig)}
        </Button>
      </Box>
    </Box>
  );
};
